import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  get details() {
    const details = this.querySelector('details');

    return details instanceof HTMLDetailsElement ? details : null;
  }

  /** @type {HTMLElement | null} */
  get summary() {
    const summary = this.details?.querySelector('summary');

    return summary instanceof HTMLElement ? summary : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #connected = false;
  /** @type {MutationObserver | null} */
  #childWatcher = null;

  connectedCallback() {
    this.#connect();
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#childWatcher?.disconnect();
    this.#childWatcher = null;
    this.#connected = false;
  }

  /**
   * Wires up the accordion, once it has the details and summary it works on. The element can
   * be handed over empty — a morphed section fills it afterwards, and a custom element is
   * upgraded before its own children are parsed — so it waits for the children rather than
   * throwing: an exception in here leaves the accordion dead and takes the rest of the
   * script with it. How many frames that wait takes is up to whoever is filling it, hence
   * watching the element instead of looking again once.
   */
  #connect = () => {
    if (this.#connected) return;

    const summary = this.summary;

    if (!summary) {
      this.#childWatcher ??= new MutationObserver(this.#connect);
      this.#childWatcher.observe(this, { childList: true, subtree: true });
      return;
    }

    this.#childWatcher?.disconnect();
    this.#childWatcher = null;
    this.#connected = true;

    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  };

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const details = this.details;

    if (!details) return;

    const isMobile = isMobileBreakpoint();

    details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      const details = this.details;

      if (!details) return;

      event.preventDefault();

      details.open = false;
      this.summary?.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
